import React, { useState, useEffect, useRef } from 'react';
import { departmentList, genderList, sectorList, maritalStatusList } from './data';
import CustomDropdown from './CustomDropdown';
import CustomDatePicker from './CustomDatePicker';
import {
  validatePranNumber,
  validateAadharNumber,
  validatePanCardNumber,
  validateMobileNo,
  validateAlternateMobileNo,
  validateEmailId,
  validateRequiredField,
  validateDOBPran,
  calculateAge,
  checkSectionAValidity
} from './ValidationUtils';

const SectionA = ({ formData: parentFormData, onDataChange, onValidationChange, onPranNumberComplete }) => {
  // Get logged in user ID
  const customerId = parentFormData?.CustomerId || '0';

  const defaultFormData = {
    ExitFormID: 0,
    CustomerId: customerId,
    PranNumber: '',
    PranName: '',
    SubscriberName: '',
    OrganizationName: '',
    Subscribergendername: '',
    FatherName: '',
    MaritalStatusname: '',
    SpouseName: '',
    SpouseGendername: '',
    DOBPran: '',
    AadharNumber: '',
    PanCardNumber: '',
    MobileNo: '',
    AlternateMobileNo: '',
    EmailId: '',
    SubscriberAddress: ''
  };

  // Initialize with parentFormData if available, otherwise use default
  const initialFormData = parentFormData && parentFormData.PranNumber
    ? { ...defaultFormData, ...parentFormData, CustomerId: customerId }
    : defaultFormData;

  const [formData, setFormData] = useState(initialFormData);

  // Validation errors state
  const [errors, setErrors] = useState({
    PranNumber: null,
    OrganizationName: null,
    PranName: null,
    SubscriberName: null,
    Subscribergendername: null,
    FatherName: null,
    AadharNumber: null,
    PanCardNumber: null,
    MobileNo: null,
    AlternateMobileNo: null,
    EmailId: null,
    SubscriberAddress: null,
    DOBPran: null
  });

  const [organisationSuggestions, setOrganisationSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [currentAge, setCurrentAge] = useState(0);

  // Helper function to convert DD-MM-YYYY to YYYY-MM-DD for validation/calculations
  const convertDDMMYYYYToYYYYMMDD = (ddmmyyyy) => {
    if (!ddmmyyyy) return '';
    const ddmmyyyyPattern = /^(\d{2})-(\d{2})-(\d{4})$/;
    if (ddmmyyyyPattern.test(ddmmyyyy)) {
      const parts = ddmmyyyy.split('-');
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    return ddmmyyyy;
  };

  // Helper function to convert various date formats to DD-MM-YYYY for display
  const convertToDDMMYYYY = (dateValue) => {
    if (!dateValue) return '';

    // YYYY-MM-DD format
    const yyyymmddPattern = /^(\d{4})-(\d{2})-(\d{2})$/;
    if (yyyymmddPattern.test(dateValue)) {
      const parts = dateValue.split('-');
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }

    // Already in DD-MM-YYYY format (2-digit-2-digit-4-digit)
    const twoDigitPattern = /^(\d{2})-(\d{2})-(\d{4})$/;
    if (twoDigitPattern.test(dateValue)) {
      return dateValue;
    }

    return dateValue;
  };

  // Ref to track if API has been called for current PRAN to prevent multiple calls
  const apiCalledRef = useRef(false);
  const lastPranNumberRef = useRef('');

  // Refs to prevent circular updates
  const currentFormDataRef = useRef(formData);
  const lastSentToParentRef = useRef(JSON.stringify(formData));
  const lastParentDataRef = useRef(JSON.stringify(parentFormData || {}));
  const isUpdatingFromParentRef = useRef(false);

  // Calculate age from stored DOBPran on mount
  useEffect(() => {
    if (formData.DOBPran) {
      const dateForCalculation = convertDDMMYYYYToYYYYMMDD(formData.DOBPran) || formData.DOBPran;
      setCurrentAge(calculateAge(dateForCalculation));
    }
  }, []); // Run only on mount

  // Update local formData when parentFormData changes (from API response)
  useEffect(() => {
    if (!parentFormData) return;

    const parentDataStr = JSON.stringify(parentFormData);
    const currentDataStr = JSON.stringify(formData);

    // Only update if parent data is different and we're not currently updating from parent
    if (parentDataStr !== lastParentDataRef.current && !isUpdatingFromParentRef.current) {
      // Check if parent has new data (like from API)
      const hasNewData = parentFormData.ExitFormID !== formData.ExitFormID ||
        parentFormData.PranNumber !== formData.PranNumber ||
        (parentFormData.PranName && parentFormData.PranName !== formData.PranName);

      if (hasNewData) {
        isUpdatingFromParentRef.current = true;
        const updatedFormData = {
          ...formData,
          ...parentFormData,
          CustomerId: customerId // Keep local customerId
        };

        setFormData(updatedFormData);
        currentFormDataRef.current = updatedFormData;
        lastParentDataRef.current = parentDataStr;

        // Validate all fields after auto-fill
        const newErrors = {
          PranNumber: validatePranNumber(updatedFormData.PranNumber),
          OrganizationName: validateRequiredField(updatedFormData.OrganizationName, 'Organisation Name'),
          PranName: validateRequiredField(updatedFormData.PranName, 'Name as per PRAN'),
          SubscriberName: validateRequiredField(updatedFormData.SubscriberName, 'Subscriber Name'),
          Subscribergendername: validateRequiredField(updatedFormData.Subscribergendername, "Subscriber's Gender"),
          FatherName: validateRequiredField(updatedFormData.FatherName, "Father's Name"),
          AadharNumber: validateAadharNumber(updatedFormData.AadharNumber),
          PanCardNumber: validatePanCardNumber(updatedFormData.PanCardNumber),
          MobileNo: validateMobileNo(updatedFormData.MobileNo),
          AlternateMobileNo: validateAlternateMobileNo(updatedFormData.AlternateMobileNo),
          EmailId: validateEmailId(updatedFormData.EmailId),
          SubscriberAddress: validateRequiredField(updatedFormData.SubscriberAddress, 'Address'),
          DOBPran: (() => {
            const dateForValidation = convertDDMMYYYYToYYYYMMDD(updatedFormData.DOBPran) || updatedFormData.DOBPran;
            return validateDOBPran(dateForValidation);
          })()
        };

        setErrors(newErrors);

        if (parentFormData.DOBPran) {
          const formattedDate = convertToDDMMYYYY(parentFormData.DOBPran);
          if (formattedDate !== parentFormData.DOBPran) {
            updatedFormData.DOBPran = formattedDate;
          }
          const dateForCalculation = convertDDMMYYYYToYYYYMMDD(formattedDate) || formattedDate;
          setCurrentAge(calculateAge(dateForCalculation));
        }

        // Trigger validation status update immediately
        const isValid = checkSectionAValidity(updatedFormData, newErrors);
        if (onValidationChange) {
          onValidationChange(isValid);
        }

        setTimeout(() => {
          isUpdatingFromParentRef.current = false;
        }, 100);
      }
    }
  }, [parentFormData, customerId]);

  // Pass data to parent component whenever it changes (but not when updating from parent)
  useEffect(() => {
    if (onDataChange && !isUpdatingFromParentRef.current) {
      const currentDataStr = JSON.stringify(formData);
      if (currentDataStr !== lastSentToParentRef.current) {
        onDataChange(formData);
        lastSentToParentRef.current = currentDataStr;
        lastParentDataRef.current = JSON.stringify(formData);
      }
    }
  }, [formData, onDataChange]);

  // Pass validation status to parent
  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(checkSectionAValidity(formData, errors));
    }
  }, [formData, errors, onValidationChange]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Update form data
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Validate based on field name
    let error = null;
    switch (name) {
      case 'PranNumber':
        // Trim whitespace from value
        const trimmedValue = value.trim();

        // Reset API call flag if PRAN number changes
        if (trimmedValue !== lastPranNumberRef.current) {
          apiCalledRef.current = false;
          lastPranNumberRef.current = trimmedValue;
        }

        error = validatePranNumber(trimmedValue);

        // Call API when PRAN number is exactly 12 digits, valid, and not already called
        if (trimmedValue.length === 12 && !error && !apiCalledRef.current && onPranNumberComplete) {
          apiCalledRef.current = true;
          onPranNumberComplete(trimmedValue);
        }
        break;
      case 'OrganizationName':
        error = validateRequiredField(value, 'Organisation Name');
        break;
      case 'PranName':
        error = validateRequiredField(value, 'Name as per PRAN');
        break;
      case 'SubscriberName':
        error = validateRequiredField(value, 'Subscriber Name');
        break;
      case 'FatherName':
        error = validateRequiredField(value, "Father's Name");
        break;
      case 'AadharNumber':
        error = validateAadharNumber(value);
        break;
      case 'PanCardNumber':
        error = validatePanCardNumber(value);
        break;
      case 'MobileNo':
        error = validateMobileNo(value);
        break;
      case 'AlternateMobileNo':
        error = validateAlternateMobileNo(value);
        break;
      case 'EmailId':
        error = validateEmailId(value);
        break;
      case 'SubscriberAddress':
        error = validateRequiredField(value, 'Address');
        break;
      case 'DOBPran':
        const dateForValidation = convertDDMMYYYYToYYYYMMDD(value) || value;
        error = validateDOBPran(dateForValidation);
        break;
      default:
        break;
    }

    // Update errors
    setErrors(prev => ({
      ...prev,
      [name]: error
    }));

    // Handle organisation name autocomplete
    if (name === 'OrganizationName') {
      if (value.length > 0) {
        const filtered = departmentList.filter(dept =>
          dept.toLowerCase().includes(value.toLowerCase())
        );
        setOrganisationSuggestions(filtered);
        setShowSuggestions(true);
      } else {
        setOrganisationSuggestions([]);
        setShowSuggestions(false);
      }
    }

    if (name === 'DOBPran' && value) {
      const dateForCalculation = convertDDMMYYYYToYYYYMMDD(value) || value;
      setCurrentAge(calculateAge(dateForCalculation));
    }
  };

  const handleSelectOrganisation = (dept) => {
    setFormData(prev => ({
      ...prev,
      OrganizationName: dept
    }));
    setErrors(prev => ({
      ...prev,
      OrganizationName: validateRequiredField(dept, 'Organisation Name')
    }));
    setShowSuggestions(false);
    setOrganisationSuggestions([]);
  };

  const handleSelectChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Validate dropdown fields
    let error = null;
    if (name === 'SubscriberName') {
      error = validateRequiredField(value, 'Subscriber Sector');
    } else if (name === 'Subscribergendername') {
      error = validateRequiredField(value, "Subscriber's Gender");
    } else if (name === 'MaritalStatusname') {
      error = validateRequiredField(value, 'Marital Status');
    } else if (name === 'SpouseGendername') {
      error = null; // Optional field
    }

    setErrors(prev => ({
      ...prev,
      [name]: error
    }));
  };

  const handleCustomDropdownChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Validate custom dropdown
    let error = null;
    // Add validation for other custom dropdowns if needed

    setErrors(prev => ({
      ...prev,
      [name]: error
    }));
  };

  return (
    <div className="section-container">
      <form>
        <div className="row g-3">
          {/* PRAN Number */}
          <div className="col-md-6">
            <label htmlFor="PranNumber" className="form-label">
              PRAN Number <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className={`form-control ${errors.PranNumber ? 'is-invalid' : ''}`}
              id="PranNumber"
              name="PranNumber"
              value={formData.PranNumber}
              onChange={handleInputChange}
              placeholder="Enter PRAN Number"
              maxLength="12"
              style={{ textTransform: 'uppercase' }}
            />
            {errors.PranNumber && (
              <div className="invalid-feedback d-block">
                {errors.PranNumber}
              </div>
            )}
          </div>

          {/* Subscriber Name */}
          <div className="col-md-6">
            <label htmlFor="SubscriberName" className="form-label">
              Subscriber Sector <span className="text-danger">*</span>
            </label>
            <select
              className={`form-select ${errors.SubscriberName ? 'is-invalid' : ''}`}
              id="SubscriberName"
              name="SubscriberName"
              value={formData.SubscriberName}
              onChange={handleSelectChange}
            >
              <option value="">Select Subscriber Sector</option>
              <option value="Government Sector">Government Sector</option>
              <option value="All India citizens/corporate">All India citizens/corporate</option>
              <option value="NPS Lite / GDS">NPS Lite / GDS</option>
            </select>
            {errors.SubscriberName && (
              <div className="invalid-feedback d-block">
                {errors.SubscriberName}
              </div>
            )}
          </div>

          {/* Organisation Name with Autocomplete */}
          <div className="col-md-6">
            <label htmlFor="organisationName" className="form-label">
              Organisation Name (PAO/DTO/CHO/NLO) <span className="text-danger">*</span>
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                className={`form-control ${errors.OrganizationName ? 'is-invalid' : ''}`}
                id="OrganizationName"
                name="OrganizationName"
                value={formData.OrganizationName}
                onChange={handleInputChange}
                placeholder="Type to search organisation..."
                autoComplete="off"
                style={{ textTransform: 'uppercase' }}
              />
              {showSuggestions && organisationSuggestions.length > 0 && (
                <div
                  className="autocomplete-suggestions"
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    backgroundColor: 'white',
                    border: '1px solid #ced4da',
                    borderTop: 'none',
                    borderRadius: '0 0 6px 6px',
                    maxHeight: '200px',
                    overflowY: 'auto',
                    zIndex: 1000,
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                  }}
                >
                  {organisationSuggestions.map((dept, index) => (
                    <div
                      key={index}
                      onClick={() => handleSelectOrganisation(dept)}
                      style={{
                        padding: '0.75rem 1rem',
                        cursor: 'pointer',
                        borderBottom: index < organisationSuggestions.length - 1 ? '1px solid #e9ecef' : 'none'
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                    >
                      {dept}
                    </div>
                  ))}
                </div>
              )}
              {errors.OrganizationName && (
                <div className="invalid-feedback d-block">
                  {errors.OrganizationName}
                </div>
              )}
            </div>
          </div>

          {/* Name as per PRAN Card */}
          <div className="col-md-6">
            <label htmlFor="PranName" className="form-label">
              Name as per PRAN Card <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className={`form-control ${errors.PranName ? 'is-invalid' : ''}`}
              id="PranName"
              name="PranName"
              value={formData.PranName}
              onChange={handleInputChange}
              placeholder="Enter name as per PRAN Card"
              style={{ textTransform: 'uppercase' }}
            />
            {errors.PranName && (
              <div className="invalid-feedback d-block">
                {errors.PranName}
              </div>
            )}
          </div>


          {/* Subscriber's Gender */}
          <div className="col-md-6">
            <label htmlFor="Subscribergendername" className="form-label">
              Subscriber's Gender <span className="text-danger">*</span>
            </label>
            <select
              className={`form-select ${errors.Subscribergendername ? 'is-invalid' : ''}`}
              id="Subscribergendername"
              name="Subscribergendername"
              value={formData.Subscribergendername}
              onChange={handleSelectChange}
            >
              <option value="">Select Gender</option>
              {genderList.map((gender, index) => (
                <option key={index} value={gender}>{gender}</option>
              ))}
            </select>
            {errors.Subscribergendername && (
              <div className="invalid-feedback d-block">
                {errors.Subscribergendername}
              </div>
            )}
          </div>

          {/* Father's Name */}
          <div className="col-md-6">
            <label htmlFor="FatherName" className="form-label">
              Father's Name <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className={`form-control ${errors.FatherName ? 'is-invalid' : ''}`}
              id="FatherName"
              name="FatherName"
              value={formData.FatherName}
              onChange={handleInputChange}
              placeholder="Enter father's name"
              style={{ textTransform: 'uppercase' }}
            />
            {errors.FatherName && (
              <div className="invalid-feedback d-block">
                {errors.FatherName}
              </div>
            )}
          </div>

          {/* Marital Status */}
          <div className="col-md-6">
            <label htmlFor="MaritalStatusname" className="form-label">
              Marital Status
            </label>
            <select
              className="form-select"
              id="MaritalStatusname"
              name="MaritalStatusname"
              value={formData.MaritalStatusname}
              onChange={handleSelectChange}
            >
              <option value="">Select Marital Status</option>
              {maritalStatusList.map((status, index) => (
                <option key={index} value={status}>{status}</option>
              ))}
            </select>
          </div>

          {/* Spouse's Name */}
          <div className="col-md-6">
            <label htmlFor="SpouseName" className="form-label">
              Spouse's Name (Only if spouse is live)
            </label>
            <input
              type="text"
              className="form-control"
              id="SpouseName"
              name="SpouseName"
              value={formData.SpouseName}
              onChange={handleInputChange}
              placeholder="Enter spouse's name"
              style={{ textTransform: 'uppercase' }}
            />
          </div>

          {/* Spouse's Gender */}
          <div className="col-md-6">
            <label htmlFor="spouseGender" className="form-label">
              Spouse's Gender
            </label>
            <select
              className="form-select"
              id="SpouseGendername"
              name="SpouseGendername"
              value={formData.SpouseGendername}
              onChange={handleSelectChange}
            >
              <option value="">Select Gender</option>
              {genderList.map((gender, index) => (
                <option key={index} value={gender}>{gender}</option>
              ))}
            </select>
          </div>

          {/* Date of Birth */}
          <div className="col-md-6">
            <label htmlFor="dateOfBirth" className="form-label">
              PRAN A/c Holder Date of Birth (As per PRAN)  <span className="text-danger">*</span>
            </label>
            <CustomDatePicker
              name="DOBPran"
              value={convertToDDMMYYYY(formData.DOBPran)}
              onChange={handleInputChange}
              maxDate={new Date().toISOString().split('T')[0]}
              className={errors.DOBPran ? 'is-invalid' : ''}
              placeholder="DD-MM-YYYY"
            />
            {currentAge > 0 && (
              <div className="mt-2" style={{ fontSize: '0.875rem', color: '#6c757d' }}>
                <i className="fas fa-info-circle me-2"></i>
                Your current age : {currentAge} years
              </div>
            )}
            {errors.DOBPran && (
              <div className="invalid-feedback d-block">
                {errors.DOBPran}
              </div>
            )}
          </div>

          {/* <div className="col-md-6">
            <label htmlFor="dateOfBirth" className="form-label">
              PRAN A/c Holder Date of Birth (As per PRAN)  <span className="text-danger">*</span>
            </label>
            <input type="date" className={`form-control ${errors.DOBPran ? 'is-invalid' : ''}`} id="DOBPran" name="DOBPran" value={formData.DOBPran} onChange={handleInputChange} />
            {currentAge > 0 && (
              <div className="mt-2" style={{ fontSize: '0.875rem', color: '#6c757d' }}>
                <i className="fas fa-info-circle me-2"></i>
                Your current age : {currentAge} years
              </div>
            )}
            {errors.DOBPran && (
              <div className="invalid-feedback d-block">
                {errors.DOBPran}
              </div>
            )}
          </div> */}

          {/* Aadhar Number */}
          <div className="col-md-6">
            <label htmlFor="AadharNumber" className="form-label">
              Aadhar Number <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className={`form-control ${errors.AadharNumber ? 'is-invalid' : ''}`}
              id="AadharNumber"
              name="AadharNumber"
              value={formData.AadharNumber}
              onChange={handleInputChange}
              placeholder="Enter Aadhar Number"
              maxLength="12"
              style={{ textTransform: 'uppercase' }}
            />
            {errors.AadharNumber && (
              <div className="invalid-feedback d-block">
                {errors.AadharNumber}
              </div>
            )}
          </div>

          {/* PAN Card Number */}
          <div className="col-md-6">
            <label htmlFor="PanCardNumber" className="form-label">
              PAN card number <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className={`form-control ${errors.PanCardNumber ? 'is-invalid' : ''}`}
              id="PanCardNumber"
              name="PanCardNumber"
              value={formData.PanCardNumber}
              onChange={handleInputChange}
              placeholder="Enter PAN card number"
              maxLength="10"
              style={{ textTransform: 'uppercase' }}
            />
            {errors.PanCardNumber && (
              <div className="invalid-feedback d-block">
                {errors.PanCardNumber}
              </div>
            )}
          </div>

          {/* Mobile Number */}
          <div className="col-md-6">
            <label htmlFor="MobileNo" className="form-label">
              Mobile Number <span className="text-danger">*</span>
            </label>
            <input
              type="tel"
              className={`form-control ${errors.MobileNo ? 'is-invalid' : ''}`}
              id="MobileNo"
              name="MobileNo"
              value={formData.MobileNo}
              onChange={handleInputChange}
              placeholder="Enter mobile number"
              maxLength="10"
              style={{ textTransform: 'uppercase' }}
            />
            {errors.MobileNo && (
              <div className="invalid-feedback d-block">
                {errors.MobileNo}
              </div>
            )}
          </div>

          {/* Alternate Phone Number */}
          <div className="col-md-6">
            <label htmlFor="AlternateMobileNo" className="form-label">
              Alternate Phone Number
            </label>
            <input
              type="tel"
              className={`form-control ${errors.AlternateMobileNo ? 'is-invalid' : ''}`}
              id="AlternateMobileNo"
              name="AlternateMobileNo"
              value={formData.AlternateMobileNo}
              onChange={handleInputChange}
              placeholder="Enter alternate phone number"
              maxLength="10"
              style={{ textTransform: 'uppercase' }}
            />
            {errors.AlternateMobileNo && (
              <div className="invalid-feedback d-block">
                {errors.AlternateMobileNo}
              </div>
            )}
          </div>

          {/* Email ID */}
          <div className="col-md-6">
            <label htmlFor="EmailId" className="form-label">
              Email ID <span className="text-danger">*</span>
            </label>
            <input
              type="email"
              className={`form-control ${errors.EmailId ? 'is-invalid' : ''}`}
              id="EmailId"
              name="EmailId"
              value={formData.EmailId}
              onChange={handleInputChange}
              placeholder="Enter email ID"
              style={{ textTransform: 'uppercase' }}
            />
            {errors.EmailId && (
              <div className="invalid-feedback d-block">
                {errors.EmailId}
              </div>
            )}
          </div>

          {/* Full Address */}
          <div className="col-md-6">
            <label htmlFor="fullAddress" className="form-label">
              Subscriber's full address with pincode <span className="text-danger">*</span>
            </label>
            <textarea
              className={`form-control ${errors.SubscriberAddress ? 'is-invalid' : ''}`}
              id="SubscriberAddress"
              name="SubscriberAddress"
              rows="3"
              value={formData.SubscriberAddress}
              onChange={handleInputChange}
              placeholder="Enter full address with pincode"
              style={{ textTransform: 'uppercase' }}
            />
            {errors.SubscriberAddress && (
              <div className="invalid-feedback d-block">
                {errors.SubscriberAddress}
              </div>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

export default SectionA;
